// src/components/dashboards/Recepsonist.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { receptionAPI, departmentAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

/* ─── Google Fonts ─── */
const injectFonts = () => {
  if (document.getElementById('opd-fonts')) return;
  const link = document.createElement('link');
  link.id   = 'opd-fonts';
  link.rel  = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap';
  document.head.appendChild(link);
};

/* ─── 27 OPD clinical department codes ─── */
const GENERAL_DEPT_CODES    = ['MED','SURG','OBGYN','PED','ORTHO','DVL','OPHT','ENT','PSY','PULM','ANES'];
const SUPER_SPECIALTY_CODES = ['CARD','NEUR','NEPH','GAST','ENDO','MONCO','HAEM','RHEUM','NEONA','PDSURG','URO','NSURG','PLAST','CTVS','SONCO','CCM'];
const CLINICAL_DEPT_CODES   = new Set([...GENERAL_DEPT_CODES, ...SUPER_SPECIALTY_CODES]);

/* ─── Queue Status definitions ─── */
const QUEUE_STATUS = {
  WAITING:         { label: 'Waiting',              color: '#0891b2', bg: '#ecfeff',  border: '#a5f3fc' },
  CALLED:          { label: 'Called',               color: '#d97706', bg: '#fffbeb',  border: '#fde68a', pulse: true },
  IN_CONSULTATION: { label: 'In Consultation',      color: '#059669', bg: '#ecfdf5',  border: '#a7f3d0' },
  IN_PROGRESS:     { label: 'In Consultation',      color: '#059669', bg: '#ecfdf5',  border: '#a7f3d0' },
  COMPLETED:       { label: 'Consultation Complete', color: '#64748b', bg: '#f8fafc',  border: '#e2e8f0' },
  SKIPPED:         { label: 'Skipped',              color: '#dc2626', bg: '#fef2f2',  border: '#fecaca' },
  CANCELLED:       { label: 'Cancelled',            color: '#dc2626', bg: '#fef2f2',  border: '#fecaca' },
  NO_SHOW:         { label: 'No Show',              color: '#dc2626', bg: '#fef2f2',  border: '#fecaca' },
};

/* ─── Backend→Frontend status normalization ─── */
const BACKEND_STATUS_MAP = {
  IN_PROGRESS: 'IN_CONSULTATION',
  NO_SHOW:     'SKIPPED',
  CANCELLED:   'SKIPPED',
};

const normalizeToken = (t) => ({
  id:              t.id,
  token_number:    t.token_number,
  patient_name:    t.patient_name || '',
  patient_id:      t.patient_id_num || t.patient_id || '',
  doctor_name:     t.doctor_name || '',
  department_id:   t.department?.id ?? t.department_id ?? t.department ?? null,
  department_name: t.department?.name || t.department_name || '',
  queue_position:  t.queue_position,
  status:          BACKEND_STATUS_MAP[t.status] || t.status || 'WAITING',
  chief_complaint: t.notes || t.chief_complaint || '',
});

/* ─── Light Palette ─── */
const C = {
  bg:        '#f1f5f9',
  bgCard:    '#ffffff',
  bgInput:   '#f8fafc',
  border:    '#e2e8f0',
  borderFocus:'#0891b2',
  teal:      '#0891b2',
  tealLight: '#ecfeff',
  tealMid:   '#cffafe',
  tealDark:  '#0e7490',
  green:     '#059669',
  greenLight:'#ecfdf5',
  yellow:    '#d97706',
  yellowLight:'#fffbeb',
  red:       '#dc2626',
  redLight:  '#fef2f2',
  text:      '#0f172a',
  textMid:   '#334155',
  muted:     '#64748b',
  mutedLight:'#94a3b8',
  white:     '#ffffff',
  sidebar:   '#ffffff',
};

/* ─── Style tokens ─── */
const S = {
  root:{ minHeight:'100vh', background:C.bg, fontFamily:"'DM Sans',sans-serif", color:C.text, display:'flex', flexDirection:'column' },
  topbar:{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:'0 2rem', height:'60px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:200, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' },
  logo:{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'1.1rem', letterSpacing:'-0.02em', color:C.teal, display:'flex', alignItems:'center', gap:'0.5rem' },
  topbarRight:{ display:'flex', alignItems:'center', gap:'1rem' },
  userTag:{ fontSize:'0.8rem', color:C.muted, background:C.bgInput, border:`1px solid ${C.border}`, borderRadius:'20px', padding:'4px 14px' },
  logoutBtn:{ background:'transparent', border:`1px solid ${C.border}`, color:C.muted, borderRadius:'8px', padding:'5px 14px', fontSize:'0.8rem', cursor:'pointer', transition:'all 0.2s' },
  layout:{ display:'flex', flex:1, height:'calc(100vh - 60px)', overflow:'hidden' },
  sidebar:{ width:'420px', minWidth:'420px', borderRight:`1px solid ${C.border}`, background:C.sidebar, display:'flex', flexDirection:'column', overflowY:'auto', padding:'1.25rem', gap:'1rem' },
  main:{ flex:1, overflowY:'auto', padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1.2rem', background:C.bg },
  card:{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'1.25rem', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' },
  cardHighlight:{ background:C.tealLight, border:`1px solid ${C.tealMid}`, borderRadius:'12px', padding:'1.25rem' },
  sectionLabel:{ fontFamily:"'Syne',sans-serif", fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:C.teal, marginBottom:'0.75rem', display:'flex', alignItems:'center', gap:'0.5rem' },
  stepBadge:(active)=>({ width:'22px', height:'22px', borderRadius:'50%', background:active?C.teal:'#f1f5f9', border:`1.5px solid ${active?C.teal:C.border}`, color:active?C.white:C.mutedLight, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:700, flexShrink:0 }),
  input:{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:'8px', padding:'9px 12px', color:C.text, fontSize:'0.88rem', width:'100%', outline:'none', fontFamily:"'DM Sans',sans-serif", transition:'border-color 0.2s', boxSizing:'border-box' },
  textarea:{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:'8px', padding:'9px 12px', color:C.text, fontSize:'0.88rem', width:'100%', outline:'none', fontFamily:"'DM Sans',sans-serif", transition:'border-color 0.2s', boxSizing:'border-box', resize:'vertical', minHeight:'72px' },
  select:{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:'8px', padding:'9px 12px', color:C.text, fontSize:'0.88rem', width:'100%', outline:'none', fontFamily:"'DM Sans',sans-serif", cursor:'pointer', boxSizing:'border-box' },
  label:{ fontSize:'0.78rem', color:C.muted, fontWeight:500, marginBottom:'4px', display:'block' },
  formGroup:{ display:'flex', flexDirection:'column', gap:'4px' },
  formGrid2:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' },
  formGrid3:{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' },
  btnPrimary:{ background:C.teal, border:'none', borderRadius:'8px', color:C.white, fontWeight:700, fontSize:'0.88rem', padding:'10px 20px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all 0.2s', width:'100%' },
  btnSecondary:{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:'8px', color:C.teal, fontWeight:600, fontSize:'0.82rem', padding:'8px 16px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all 0.2s' },
  btnGreen:{ background:C.green, border:'none', borderRadius:'8px', color:C.white, fontWeight:700, fontSize:'0.88rem', padding:'10px 20px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all 0.2s', width:'100%' },
  patientResult:{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.75rem', background:C.white, border:`1.5px solid ${C.border}`, borderRadius:'10px', cursor:'pointer', transition:'all 0.2s', marginBottom:'0.5rem' },
  patientAvatar:{ width:'38px', height:'38px', borderRadius:'50%', background:C.teal, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.95rem', color:C.white, flexShrink:0 },
  doctorCard:(selected,disabled)=>({ padding:'1rem', background:selected?C.tealLight:C.white, border:`1.5px solid ${selected?C.teal:C.border}`, borderRadius:'10px', cursor:disabled?'not-allowed':'pointer', transition:'all 0.2s', opacity:disabled?0.5:1, marginBottom:'0.6rem' }),
  statusDot:(status)=>({ width:'8px', height:'8px', borderRadius:'50%', background:status==='AVAILABLE'?C.green:status==='IN_CONSULTATION'?C.yellow:C.mutedLight, flexShrink:0 }),
  tokenCard:{ background:`linear-gradient(135deg, ${C.teal} 0%, ${C.tealDark} 100%)`, borderRadius:'16px', padding:'2rem', textAlign:'center', position:'relative', overflow:'hidden' },
  tokenNumber:{ fontFamily:"'Syne',sans-serif", fontSize:'4rem', fontWeight:800, color:C.white, letterSpacing:'-0.02em', lineHeight:1 },
  badge:(color)=>({ display:'inline-flex', alignItems:'center', gap:'4px', padding:'3px 10px', borderRadius:'20px', fontSize:'0.72rem', fontWeight:600,
    background:color==='green'?C.greenLight:color==='yellow'?C.yellowLight:color==='red'?C.redLight:C.tealLight,
    color:color==='green'?C.green:color==='yellow'?C.yellow:color==='red'?C.red:C.teal,
    border:`1px solid ${color==='green'?'#a7f3d0':color==='yellow'?'#fde68a':color==='red'?'#fecaca':C.tealMid}` }),
  live:{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'0.7rem', fontWeight:700, color:C.green, letterSpacing:'0.06em' },
  liveDot:{ width:'7px', height:'7px', borderRadius:'50%', background:C.green, animation:'pulse 1.4s ease-in-out infinite' },
  errorBox:{ background:C.redLight, border:'1px solid #fecaca', borderRadius:'8px', padding:'8px 12px', fontSize:'0.82rem', color:C.red, marginBottom:'0.75rem' },
  fieldHint:{ fontSize:'0.72rem', color:C.mutedLight, marginTop:'2px' },
};

/* ─── Keyframes ─── */
const injectKeyframes = () => {
  if (document.getElementById('opd-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'opd-keyframes';
  style.textContent = `
    @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.85)}}
    @keyframes pulseGlow{0%,100%{box-shadow:0 0 0 0 rgba(217,119,6,0.4)}50%{box-shadow:0 0 0 8px rgba(217,119,6,0)}}
    @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
    @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    @keyframes tokenPop{0%{transform:scale(.88);opacity:0}60%{transform:scale(1.03)}100%{transform:scale(1);opacity:1}}
    @keyframes nowServing{0%,100%{opacity:1}50%{opacity:0.55}}
    @keyframes modalIn{from{opacity:0;transform:scale(0.97) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
    .opd-input:focus{border-color:#0891b2!important;box-shadow:0 0 0 3px rgba(8,145,178,0.1)!important}
    .opd-btn-primary:hover{background:#0e7490!important;transform:translateY(-1px);box-shadow:0 4px 14px rgba(8,145,178,0.25)}
    .opd-btn-secondary:hover{background:#f0fdfe!important;border-color:#0891b2!important}
    .opd-btn-green:hover{background:#047857!important;transform:translateY(-1px)}
    .opd-patient-result:hover{border-color:#0891b2!important;background:#f0fdfe!important}
    .opd-doctor-card:hover:not(.disabled){border-color:#0891b2!important;box-shadow:0 2px 10px rgba(8,145,178,0.12)!important}
    .opd-queue-dept-card:hover{border-color:#0891b2!important;box-shadow:0 2px 8px rgba(8,145,178,0.1)!important;transform:translateY(-1px);cursor:pointer}
    .opd-token-anim{animation:tokenPop .45s cubic-bezier(.22,1,.36,1) forwards}
    .opd-fade-in{animation:fadeIn .28s ease forwards}
    .opd-slide-up{animation:slideUp .3s ease forwards}
    .opd-now-serving{animation:nowServing 2s ease-in-out infinite}
    .opd-called-pulse{animation:pulseGlow 1.5s ease-in-out infinite}
    .opd-logout-btn:hover{border-color:#0891b2!important;color:#0891b2!important}
    select optgroup{background:#fff;font-weight:700;font-size:0.75rem;letter-spacing:0.04em;color:#64748b}
    select option{background:#fff;color:#0f172a}
    .modal-overlay{animation:fadeIn .2s ease forwards}
    .modal-box{animation:modalIn .22s cubic-bezier(.22,1,.36,1) forwards}
    .queue-row-enter{animation:slideUp .18s ease forwards}
    ::-webkit-scrollbar{width:5px;height:5px}
    ::-webkit-scrollbar-track{background:#f1f5f9}
    ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px}
    ::-webkit-scrollbar-thumb:hover{background:#94a3b8}
    .section-divider{font-size:0.7rem;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.08em;margin:0.7rem 0 0.5rem;padding-bottom:0.35rem;border-bottom:1px solid #f1f5f9}
  `;
  document.head.appendChild(style);
};

/* ─── Validation helpers ─── */
const validateAadhar = (v) => {
  if (!v) return null;
  const d = v.replace(/[\s-]/g, '');
  if (!/^\d{12}$/.test(d)) return 'Must be exactly 12 digits';
  return null;
};
const validateABHA = (v) => {
  if (!v) return null;
  const d = v.replace(/[-\s]/g, '');
  if (!/^\d{14}$/.test(d)) return 'Must be 14 digits (XX-XXXX-XXXX-XXXX)';
  return null;
};
const formatAadhar = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 12);
  if (d.length <= 4) return d;
  if (d.length <= 8) return `${d.slice(0,4)}-${d.slice(4)}`;
  return `${d.slice(0,4)}-${d.slice(4,8)}-${d.slice(8)}`;
};
const formatABHA = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 6)  return `${d.slice(0,2)}-${d.slice(2)}`;
  if (d.length <= 10) return `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
  return `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6,10)}-${d.slice(10)}`;
};

/* ─── OPD Slip HTML generator ─── */
const generateSlipHTML = (opdRecord, tokenData, deptName, doctorName, patientObj) => {
  const now = new Date();
  const dateStr  = opdRecord?.visit_date
    ? opdRecord.visit_date.split('-').reverse().join('-')
    : `${now.getDate().toString().padStart(2,'0')}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getFullYear()}`;
  const timeStr  = opdRecord?.visit_time
    ? opdRecord.visit_time.slice(0,5)
    : `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`;
  const patientName  = opdRecord?.patient_name_snapshot || (patientObj ? `${patientObj.first_name} ${patientObj.last_name}` : '');
  const mrNo = tokenData?.patient_id_num || patientObj?.patient_id || tokenData?.patient_id || 'UNKNOWN';
  const phone     = opdRecord?.patient_phone  || patientObj?.phone || '';
  const nextOfKin = opdRecord?.next_of_kin    || patientObj?.emergency_contact_name || '';
  const ageNum    = opdRecord?.patient_age    || patientObj?.age;
  const age       = ageNum ? `${ageNum} Y` : '';
  const genderRaw = opdRecord?.patient_gender || patientObj?.gender || '';
  const genderDisp = genderRaw === 'M' ? 'Male' : genderRaw === 'F' ? 'Female' : genderRaw || '';
  const genderCode = genderRaw === 'M' ? 'M' : genderRaw === 'F' ? 'F' : genderRaw;
  const ageGenderShort = [ageNum ? `${ageNum}Y` : '', genderCode].filter(Boolean).join('/');
  const aadharRaw = opdRecord?.patient_aadhar || patientObj?.aadhar_number || '';
  const aadhar    = aadharRaw.length === 12
    ? `${aadharRaw.slice(0,4)}-${aadharRaw.slice(4,8)}-${aadharRaw.slice(8)}`
    : aadharRaw;
  const abha      = opdRecord?.patient_abha   || patientObj?.abha_number || '';
  const dept      = opdRecord?.department_name || deptName || '';
  const address   = (opdRecord?.patient_address || patientObj?.address || '').replace(/\n/g,'<br>');
  const tokenNum  = opdRecord?.token_number   || tokenData?.token_number || '';
  const doctorDisp = opdRecord?.doctor_name   || doctorName || '';
  const mrDisplay = mrNo && mrNo !== 'UNKNOWN' ? mrNo : tokenNum;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>OPD Slip – ${tokenNum}</title>
<style>
  @page { size: A4; margin: 10mm 16mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; color: #000; background:#fff; }
  .header { text-align: center; padding-bottom: 8px; border-bottom: 3px double #000; margin-bottom: 10px; }
  .header h1 { font-size: 20pt; font-weight: 900; letter-spacing: 1.5px; text-transform: uppercase; }
  .header .sub1 { font-size: 9.5pt; font-weight: bold; margin-top: 3px; color: #222; }
  .header .address { font-size: 8.5pt; margin-top: 2px; color: #444; }
  .header .phone { font-size: 8pt; margin-top: 3px; color: #555; }
  .title-box { border: 2.5px solid #000; display: inline-block; padding: 4px 32px; margin-top: 8px; font-size: 12pt; font-weight: 900; letter-spacing: 3px; text-transform: uppercase; background: #f5f5f5; }
  .name-bar { display: flex; justify-content: space-between; align-items: flex-end; margin: 10px 0 8px; border-bottom: 1.5px solid #999; padding-bottom: 6px; }
  .name-bar .pname { font-size: 15pt; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; }
  .name-bar .pname .age-gender { font-size: 11pt; font-weight: 700; margin-left: 10px; color: #333; }
  .name-bar .dt { font-size: 9pt; font-weight: bold; text-align: right; color: #222; line-height: 1.5; }
  .grid { width: 100%; border-collapse: collapse; margin-top: 4px; }
  .grid td { padding: 3.5px 0; vertical-align: top; font-size: 10pt; line-height: 1.45; }
  .grid td.lbl  { width: 26%; font-weight: bold; white-space: nowrap; color: #111; }
  .grid td.sep  { width: 3%;  text-align: center; color: #333; }
  .grid td.val  { width: 26%; word-break: break-word; }
  .grid td.gap  { width: 5%; }
  .grid td.lbl2 { width: 16%; font-weight: bold; white-space: nowrap; color: #111; }
  .grid td.sep2 { width: 3%;  text-align: center; }
  .grid td.val2 { width: 21%; }
  .divider { border: none; border-top: 1px solid #aaa; margin: 10px 0; }
  .footer { margin-top: 8px; text-align: center; }
  .footer .note { font-size: 8.5pt; color: #555; line-height: 1.6; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
<div class="header">
  <h1>XYZ HOSPITAL</h1>
  <div class="sub1">Medical College Hospital &amp; Research Institute</div>
  <div class="address">123 Hospital Road, City – 000 000 &nbsp;|&nbsp; www.xyzhospital.org</div>
  <div class="phone">☎ 000-00000000 &nbsp;|&nbsp; Emergency : 108 &nbsp;|&nbsp; info@xyzhospital.org</div>
  <div><span class="title-box">Out Patient Record</span></div>
</div>
<div class="name-bar">
  <div>
    <div style="font-size:7.5pt;color:#666;margin-bottom:2px;text-transform:uppercase;letter-spacing:0.5px;">Patient Name</div>
    <div class="pname">${patientName.toUpperCase()}<span class="age-gender">${ageGenderShort ? `&nbsp;&nbsp;${ageGenderShort}` : ''}</span></div>
  </div>
  <div class="dt">
    <div>Date&nbsp;: <strong>${dateStr}</strong></div>
    <div>Time&nbsp;: <strong>${timeStr}</strong></div>
  </div>
</div>
<table class="grid">
  <tr>
    <td class="lbl">Patient ID</td><td class="sep">:</td><td class="val"><strong>${mrDisplay}</strong></td>
    <td class="gap"></td>
    <td class="lbl2">Mobile No</td><td class="sep2">:</td><td class="val2">${phone}</td>
  </tr>
  <tr>
    <td class="lbl">Age / Gender</td><td class="sep">:</td><td class="val">${age}${age && genderDisp ? ' / ' : ''}${genderDisp}</td>
    <td class="gap"></td>
    <td class="lbl2">Aadhar No</td><td class="sep2">:</td><td class="val2" style="font-size:9pt;">${aadhar}</td>
  </tr>
  <tr>
    <td class="lbl">Next Of Kin</td><td class="sep">:</td><td class="val">${nextOfKin}</td>
    <td class="gap"></td>
    <td class="lbl2">ABHA No</td><td class="sep2">:</td><td class="val2" style="font-size:9pt;">${abha}</td>
  </tr>
  <tr>
    <td class="lbl">Department</td><td class="sep">:</td><td class="val" colspan="4">${dept}</td>
  </tr>
  <tr>
    <td class="lbl">Consulting Doctor</td><td class="sep">:</td><td class="val" colspan="4">${doctorDisp}</td>
  </tr>
  <tr>
    <td class="lbl">Address</td><td class="sep">:</td><td class="val" colspan="4" style="padding-top:3px;">${address || '—'}</td>
  </tr>
</table>
<hr class="divider">
<div class="footer">
  <div class="note"><strong>Date : ${dateStr}</strong></div>
</div>
</body>
</html>`;
};

const printOPDSlip = (opdRecord, tokenData, deptName, doctorName, patientObj) => {
  return new Promise((resolve) => {
    const html   = generateSlipHTML(opdRecord, tokenData, deptName, doctorName, patientObj);
    const target = 'opd_record_' + Date.now();
    const win    = window.open('', target, 'width=870,height=820,menubar=yes,toolbar=yes');
    if (!win) { notify.info('Please allow pop-ups to print the OPD slip.'); resolve(); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    const done = () => { clearTimeout(fallback); try { win.close(); } catch {} resolve(); };
    const fallback = setTimeout(done, 120_000);
    win.addEventListener('afterprint', done);
    const doPrint = () => { try { win.print(); } catch {} };
    win.addEventListener('load', () => setTimeout(doPrint, 600));
    setTimeout(() => { if (win.document?.readyState === 'complete') setTimeout(doPrint, 300); }, 1800);
  });
};

/* ─── Patient Card HTML generator ─── */
const generatePatientCardHTML = (opdRecord, tokenData, deptName, patientObj, copies = 1) => {
  const patientName = opdRecord?.patient_name_snapshot || (patientObj ? `${patientObj.first_name} ${patientObj.last_name}` : '');
  const mrNo        = tokenData?.patient_id_num || patientObj?.patient_id || tokenData?.patient_id || 'UNKNOWN';
  const phone       = opdRecord?.patient_phone  || patientObj?.phone || '';
  const ageNum      = opdRecord?.patient_age    || patientObj?.age;
  const genderRaw   = opdRecord?.patient_gender || patientObj?.gender || '';
  const genderCode  = genderRaw === 'M' ? 'M' : genderRaw === 'F' ? 'F' : genderRaw;
  const ageGender   = [ageNum ? `${ageNum}Y` : '', genderCode].filter(Boolean).join('/');
  const aadharRaw   = opdRecord?.patient_aadhar || patientObj?.aadhar_number || '';
  const aadhar      = aadharRaw.length === 12
    ? `${aadharRaw.slice(0,4)}-${aadharRaw.slice(4,8)}-${aadharRaw.slice(8)}`
    : aadharRaw;
  const dept        = opdRecord?.department_name || deptName || '';
  const tokenNum    = opdRecord?.token_number   || tokenData?.token_number || '';
  const barcodeVal  = mrNo && mrNo !== 'UNKNOWN' ? mrNo : (tokenNum || 'PATIENT');

  const pages = Array.from({ length: copies }, (_, i) => `
    <div class="card-page${i < copies - 1 ? ' page-break' : ''}">
      <div class="card">
        <div class="card-left">
          <svg class="barcode-svg barcode-${i}"></svg>
          <div class="barcode-id">${mrNo}</div>
        </div>
        <div class="card-right">
          <div class="card-label">PATIENT CARD</div>
          <div class="card-name">${patientName.toUpperCase()}</div>
          <div class="card-row">${ageGender}${ageGender && phone ? '  |  ' : ''}${phone ? `📞 ${phone}` : ''}</div>
          <div class="card-row">Dept: ${dept}${tokenNum ? `  |  Token: <strong>${tokenNum}</strong>` : ''}</div>
          ${aadhar ? `<div class="card-row aadhar">Aadhar: ${aadhar}</div>` : ''}
        </div>
      </div>
      <div class="copy-note">Copy ${i + 1} of ${copies}</div>
    </div>`).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Patient Card – ${mrNo}</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
<style>
  @page { size: A4; margin: 25mm 20mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #000; }
  .card-page { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60mm; }
  .page-break { page-break-after: always; break-after: page; }
  .card { width: 160mm; border: 2.5px solid #000; border-radius: 8px; display: flex; align-items: stretch; overflow: hidden; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
  .card-left { flex: 0 0 auto; width: 58mm; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 8mm 6mm; border-right: 1.5px solid #ccc; background: #fff; }
  .barcode-svg { display: block; width: 100%; height: auto; }
  .barcode-id { font-family: 'Courier New', monospace; font-size: 8pt; font-weight: 900; letter-spacing: 2.5px; margin-top: 4px; color: #000; text-align: center; }
  .card-right { flex: 1; padding: 7mm 8mm; display: flex; flex-direction: column; justify-content: center; gap: 2.5mm; }
  .card-label { font-size: 7pt; font-weight: bold; color: #888; text-transform: uppercase; letter-spacing: 1.2px; margin-bottom: 1mm; }
  .card-name { font-size: 14pt; font-weight: 900; letter-spacing: 0.3px; line-height: 1.1; }
  .card-row { font-size: 9.5pt; color: #222; line-height: 1.4; }
  .aadhar { font-size: 8pt; color: #555; }
  .copy-note { margin-top: 5mm; font-size: 7.5pt; color: #aaa; text-align: center; letter-spacing: 0.5px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
${pages}
<script>
  var BARCODE_VAL = '${barcodeVal.replace(/'/g, "\\'")}';
  var COPIES = ${copies};
  function renderAllBarcodes() {
    if (typeof JsBarcode === 'undefined') { setTimeout(renderAllBarcodes, 150); return; }
    for (var i = 0; i < COPIES; i++) {
      var svg = document.querySelector('.barcode-' + i);
      if (!svg) continue;
      try { JsBarcode(svg, BARCODE_VAL, { format:'CODE128', width:1.8, height:52, displayValue:false, margin:0, background:'#ffffff', lineColor:'#000000' }); } catch(e) {}
    }
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', renderAllBarcodes); } else { renderAllBarcodes(); }
  window.addEventListener('load', renderAllBarcodes);
</script>
</body>
</html>`;
};

const printPatientCard = (opdRecord, tokenData, deptName, patientObj, copies = 1) => {
  return new Promise((resolve) => {
    const html   = generatePatientCardHTML(opdRecord, tokenData, deptName, patientObj, copies);
    const target = 'patient_card_' + Date.now();
    const win    = window.open('', target, 'width=760,height=720,menubar=yes,toolbar=yes');
    if (!win) { notify.info('Please allow pop-ups to print the patient card.'); resolve(); return; }
    win.document.open(); win.document.write(html); win.document.close(); win.focus();
    const done   = () => { clearTimeout(fallback); resolve(); };
    const fallback = setTimeout(done, 120_000);
    win.addEventListener('afterprint', done);
    const doPrint = () => { try { win.print(); } catch {} };
    win.addEventListener('load', () => setTimeout(doPrint, 1000));
    setTimeout(() => { if (win.document?.readyState === 'complete') setTimeout(doPrint, 600); }, 2500);
  });
};

/* ════════════════════════════════════════════════════════════════════════════ */
/*  BARCODE SCANNER / PATIENT LOOKUP MODAL                                      */
/* ════════════════════════════════════════════════════════════════════════════ */
const PatientCardScanModal = ({ onClose }) => {
  const [query, setQuery]     = useState('');
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [error, setError]     = useState('');
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);

  const lookup = useCallback(async (val) => {
    const id = (val || query).trim();
    if (!id) return;
    setLoading(true); setError(''); setPatient(null); setRecords([]);
    try {
      const results = await receptionAPI.searchPatient(id);
      const list    = Array.isArray(results) ? results : results?.results || [];
      const exact   = list.find(p => p.patient_id === id || p.id === id) || list[0];
      if (!exact) { setError(`No patient found for ID: ${id}`); setLoading(false); return; }
      setPatient(exact);
      try {
        const hist = await receptionAPI.getPatientOPDHistory(exact.id);
        setRecords(Array.isArray(hist) ? hist : hist?.results || []);
      } catch { setRecords([]); }
    } catch (e) {
      setError(e?.response?.data?.error || 'Lookup failed. Please try again.');
    } finally { setLoading(false); }
  }, [query]);

  const handleKey = (e) => { if (e.key === 'Enter') lookup(query); };
  const genderLabel = (g) => g === 'M' ? 'Male' : g === 'F' ? 'Female' : g || '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', backdropFilter:'blur(4px)' }}>
      <div style={{ width:'100%', maxWidth:'680px', maxHeight:'90vh', background:C.white, border:`1px solid ${C.border}`, borderRadius:'16px', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.18)' }}>

        {/* Header */}
        <div style={{ padding:'1.25rem 1.5rem', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
            <div style={{ width:38, height:38, borderRadius:'10px', background:C.tealLight, border:`1px solid ${C.tealMid}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem' }}>🔍</div>
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'1rem', color:C.text }}>Barcode / Patient Lookup</div>
              <div style={{ fontSize:'0.72rem', color:C.muted }}>Scan a patient card barcode or type the Patient ID</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:C.bgInput, border:`1px solid ${C.border}`, borderRadius:'8px', color:C.muted, padding:'6px 14px', fontSize:'0.8rem', cursor:'pointer' }}>✕ Close</button>
        </div>

        {/* Search */}
        <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKey}
              placeholder="Scan barcode or type Patient ID (e.g. PAT000001)…"
              style={{ flex:1, ...S.input }} className="opd-input" />
            <button onClick={() => lookup(query)} disabled={loading || !query.trim()}
              style={{ background:C.teal, color:C.white, border:'none', borderRadius:'8px', padding:'10px 22px', fontWeight:700, fontSize:'0.88rem', cursor:'pointer', opacity: loading || !query.trim() ? 0.5 : 1 }}>
              {loading ? '…' : 'Search'}
            </button>
          </div>
          {error && <div style={{ color:C.red, fontSize:'0.8rem', marginTop:'8px' }}>⚠ {error}</div>}
        </div>

        {/* Results */}
        <div style={{ flex:1, overflowY:'auto', padding:'1rem 1.5rem' }}>
          {loading && (
            <div style={{ textAlign:'center', padding:'2rem', color:C.muted }}>
              <div style={{ fontSize:'1.5rem', marginBottom:'0.5rem' }}>⏳</div>
              Fetching patient records…
            </div>
          )}

          {patient && !loading && (
            <>
              <div style={{ background:C.tealLight, border:`1px solid ${C.tealMid}`, borderRadius:'12px', padding:'1rem 1.25rem', marginBottom:'1rem' }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontSize:'0.65rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Patient Profile</div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'1.2rem', color:C.text }}>{patient.first_name} {patient.last_name}</div>
                    <div style={{ fontSize:'0.8rem', color:C.teal, fontWeight:600, marginTop:'2px' }}>{patient.patient_id}</div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 20px', fontSize:'0.8rem', color:C.textMid }}>
                    {[['Age', patient.age ? `${patient.age} yrs` : '—'],['Gender', genderLabel(patient.gender)],['Blood', patient.blood_group || '—'],['Phone', patient.phone || '—']].map(([k,v]) => (
                      <div key={k} style={{ display:'flex', gap:'6px' }}>
                        <span style={{ color:C.muted, minWidth:'50px' }}>{k}:</span>
                        <span style={{ color:C.text, fontWeight:500 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop:'0.75rem', paddingTop:'0.75rem', borderTop:`1px solid ${C.tealMid}`, display:'flex', flexDirection:'column', gap:'6px', fontSize:'0.78rem' }}>
                  {(() => {
                    const hasAllergy = patient.allergies && patient.allergies.trim() && patient.allergies.toLowerCase() !== 'none';
                    return (
                      <div style={{ background: hasAllergy ? C.redLight : C.bgInput, border:`1px solid ${hasAllergy ? '#fecaca' : C.border}`, borderRadius:'8px', padding:'6px 10px', display:'flex', alignItems:'flex-start', gap:'8px' }}>
                        <span style={{ fontSize:'0.88rem', flexShrink:0 }}>{hasAllergy ? '🚨' : '✅'}</span>
                        <div>
                          <div style={{ fontSize:'0.62rem', color: hasAllergy ? C.red : C.muted, textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:700, marginBottom:'2px' }}>Allergies</div>
                          <div style={{ color: hasAllergy ? C.red : C.muted, fontWeight: hasAllergy ? 700 : 400 }}>
                            {hasAllergy ? patient.allergies : 'No known allergies'}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {patient.current_medications?.trim() && (
                    <div style={{ background:C.greenLight, border:'1px solid #a7f3d0', borderRadius:'8px', padding:'6px 10px' }}>
                      <div style={{ fontSize:'0.62rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'2px' }}>Current Medications</div>
                      <div style={{ color:C.green }}>{patient.current_medications}</div>
                    </div>
                  )}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 20px' }}>
                    {[['Email', patient.email || '—'],['Aadhar', patient.aadhar_number ? `${patient.aadhar_number.slice(0,4)}-${patient.aadhar_number.slice(4,8)}-${patient.aadhar_number.slice(8)}` : '—'],['ABHA', patient.abha_number || '—'],['Prev. Conditions', patient.previous_conditions || '—'],['Address', patient.address || '—']].map(([k,v]) => (
                      <div key={k} style={{ display:'flex', gap:'6px', alignItems:'flex-start' }}>
                        <span style={{ color:C.muted, minWidth:'80px', flexShrink:0 }}>{k}:</span>
                        <span style={{ color:C.text }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'0.8rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.6rem' }}>
                OPD Visit History ({records.length})
              </div>

              {records.length === 0 ? (
                <div style={{ textAlign:'center', padding:'1.5rem', color:C.mutedLight, fontSize:'0.85rem', background:C.bgInput, borderRadius:'10px', border:`1px dashed ${C.border}` }}>
                  No previous OPD records found
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
                  {records.map((rec, i) => {
                    const statusBg  = { COMPLETED:C.greenLight, IN_CONSULTATION:C.greenLight, WAITING:C.tealLight, IN_PROGRESS:C.yellowLight, CANCELLED:C.redLight, NO_SHOW:C.redLight };
                    const statusTxt = { COMPLETED:C.green, IN_CONSULTATION:C.green, WAITING:C.teal, IN_PROGRESS:C.yellow, CANCELLED:C.red, NO_SHOW:C.red };
                    const st = rec.status || 'COMPLETED';
                    let rxMeds = [];
                    if (rec.prescription) {
                      if (Array.isArray(rec.prescription)) rxMeds = rec.prescription;
                      else { try { rxMeds = JSON.parse(rec.prescription); } catch { rxMeds = []; } }
                    }
                    return (
                      <div key={rec.id || i} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:'12px', overflow:'hidden' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.6rem 1rem', borderBottom:`1px solid ${C.border}`, background:C.bgInput, flexWrap:'wrap', gap:'6px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'0.9rem', color:C.text }}>{rec.token_number || `Visit #${records.length - i}`}</span>
                            <span style={{ fontSize:'0.68rem', fontWeight:700, color:statusTxt[st]||C.muted, background:statusBg[st]||C.bgInput, padding:'2px 10px', borderRadius:'20px', border:`1px solid ${statusTxt[st]||C.border}` }}>{st}</span>
                          </div>
                          <div style={{ display:'flex', gap:'14px', fontSize:'0.74rem', color:C.muted }}>
                            {(rec.doctor_name || rec.doctor) && <span>Dr. {rec.doctor_name || rec.doctor}</span>}
                            {(rec.department_name || rec.department) && <span>{rec.department_name || rec.department}</span>}
                            <span>{fmtDate(rec.visit_date || rec.created_at)}</span>
                          </div>
                        </div>
                        <div style={{ padding:'0.75rem 1rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                          {rec.chief_complaint && (
                            <div>
                              <div style={{ fontSize:'0.62rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'3px' }}>Chief Complaint</div>
                              <div style={{ fontSize:'0.82rem', color:C.textMid, fontStyle:'italic' }}>"{rec.chief_complaint}"</div>
                            </div>
                          )}
                          {rec.diagnosis && (
                            <div>
                              <div style={{ fontSize:'0.62rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'3px' }}>Diagnosis</div>
                              <div style={{ fontSize:'0.84rem', color:C.text, fontWeight:600 }}>{rec.diagnosis}</div>
                            </div>
                          )}
                          {rxMeds.length > 0 && (
                            <div>
                              <div style={{ fontSize:'0.62rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'4px' }}>Prescription</div>
                              <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                                {rxMeds.map((m, mi) => (
                                  <div key={mi} style={{ fontSize:'0.78rem', background:C.greenLight, border:'1px solid #a7f3d0', borderRadius:'6px', padding:'4px 10px', display:'flex', flexWrap:'wrap', gap:'8px', color:C.green }}>
                                    <strong style={{ color:C.text }}>{m.name || m.drug_name || '—'}</strong>
                                    {m.dosage && <span>{m.dosage}</span>}
                                    {m.frequency && <span>· {m.frequency}</span>}
                                    {m.duration  && <span>· {m.duration}</span>}
                                    {m.instructions && <span style={{ color:C.muted, fontStyle:'italic' }}>({m.instructions})</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {rec.advice && (
                            <div>
                              <div style={{ fontSize:'0.62rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'3px' }}>Advice</div>
                              <div style={{ fontSize:'0.82rem', color:C.textMid }}>{rec.advice}</div>
                            </div>
                          )}
                          {rec.follow_up_date && (
                            <div style={{ background:C.tealLight, border:`1px solid ${C.tealMid}`, borderRadius:'6px', padding:'5px 10px' }}>
                              <span style={{ fontSize:'0.78rem', color:C.teal }}>📅 Follow-up: {fmtDate(rec.follow_up_date)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {!loading && !patient && !error && (
            <div style={{ textAlign:'center', padding:'2.5rem 1rem', color:C.mutedLight }}>
              <div style={{ fontSize:'3rem', marginBottom:'0.75rem' }}>📷</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'1rem', color:C.textMid }}>Scan or Search</div>
              <div style={{ fontSize:'0.82rem', marginTop:'0.4rem' }}>Point a barcode scanner at a patient card, or type the Patient ID above</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
/*  QUEUE DETAIL MODAL                                                          */
/* ════════════════════════════════════════════════════════════════════════════ */
const QueueDetailModal = ({ dept, onClose, avgConsultMins = 10 }) => {
  const [queueData, setQueueData] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const pollRef = useRef(null);

  const fetchQueue = useCallback(async () => {
    if (!dept?.id) return;
    try {
      const data = await receptionAPI.getTokens({ department_id: dept.id });
      const list = Array.isArray(data) ? data : data?.results || [];
      setQueueData(list.map(normalizeToken));
    } catch { setQueueData([]); }
    finally { setLoading(false); }
  }, [dept?.id]);

  useEffect(() => {
    fetchQueue();
    pollRef.current = setInterval(fetchQueue, 10_000);
    return () => clearInterval(pollRef.current);
  }, [fetchQueue]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const currentToken  = queueData.find(q => q.status === 'IN_CONSULTATION' || q.status === 'CALLED');
  const waitingTokens = queueData.filter(q => q.status === 'WAITING');
  const allFiltered   = filterStatus === 'ALL' ? queueData : queueData.filter(q => q.status === filterStatus);
  const statusCounts  = queueData.reduce((acc, q) => { acc[q.status] = (acc[q.status] || 0) + 1; return acc; }, {});
  const getEstWait    = (idx) => Math.round(waitingTokens.slice(0, idx).length * avgConsultMins + (currentToken ? avgConsultMins / 2 : avgConsultMins));

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', backdropFilter:'blur(4px)' }}>
      <div className="modal-box" style={{ width:'100%', maxWidth:'820px', maxHeight:'88vh', background:C.white, border:`1px solid ${C.border}`, borderRadius:'16px', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>

        {/* Header */}
        <div style={{ padding:'1.25rem 1.5rem', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:C.white }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'4px' }}>
              <span style={S.live}><span style={S.liveDot}/>LIVE</span>
              <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'1rem', color:C.text }}>{dept?.name}</span>
            </div>
            <div style={{ fontSize:'0.75rem', color:C.muted }}>
              {queueData.length} total · {statusCounts.WAITING || 0} waiting · {statusCounts.IN_CONSULTATION || 0} in consultation
            </div>
          </div>
          <button onClick={onClose} style={{ background:C.bgInput, border:`1px solid ${C.border}`, borderRadius:'8px', color:C.muted, padding:'6px 14px', fontSize:'0.8rem', cursor:'pointer' }}>✕ Close</button>
        </div>

        {/* Stats row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1px', background:C.border, borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
          {[{ key:'WAITING', icon:'⏳', label:'Waiting' },{ key:'CALLED', icon:'📢', label:'Called' },{ key:'IN_CONSULTATION', icon:'🩺', label:'In Consult' },{ key:'COMPLETED', icon:'✅', label:'Completed' }].map(({ key, icon, label }) => {
            const def = QUEUE_STATUS[key];
            return (
              <div key={key} style={{ background:C.white, padding:'0.85rem 1rem', textAlign:'center' }}>
                <div style={{ fontSize:'1.1rem', marginBottom:'2px' }}>{icon}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'1.4rem', fontWeight:800, color:def.color }}>{statusCounts[key] || 0}</div>
                <div style={{ fontSize:'0.68rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</div>
              </div>
            );
          })}
        </div>

        {/* Current & Next */}
        {(currentToken || waitingTokens.length > 0) && (
          <div style={{ padding:'1rem 1.5rem', background:C.bgInput, borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
            <div style={{ display:'grid', gridTemplateColumns: currentToken ? '1fr 2fr' : '1fr', gap:'1rem', alignItems:'start' }}>
              {currentToken && (
                <div style={{ background: currentToken.status==='CALLED' ? C.yellowLight : C.greenLight, border:`1px solid ${currentToken.status==='CALLED' ? '#fde68a' : '#a7f3d0'}`, borderRadius:'12px', padding:'1rem', textAlign:'center' }}>
                  <div style={{ fontSize:'0.65rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>
                    {currentToken.status === 'CALLED' ? '📢 Now Called' : '🩺 Now Serving'}
                  </div>
                  <div className={currentToken.status==='IN_CONSULTATION' ? 'opd-now-serving' : ''} style={{ fontFamily:"'Syne',sans-serif", fontSize:'2.4rem', fontWeight:800, color: currentToken.status==='CALLED' ? C.yellow : C.green, lineHeight:1 }}>
                    {currentToken.token_number}
                  </div>
                  <div style={{ fontSize:'0.78rem', color:C.textMid, marginTop:'6px', fontWeight:500 }}>{currentToken.patient_name}</div>
                  <div style={{ fontSize:'0.7rem', color:C.muted }}>{currentToken.doctor_name || ''}</div>
                </div>
              )}
              {waitingTokens.length > 0 && (
                <div>
                  <div style={{ fontSize:'0.65rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>Next in Queue</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                    {waitingTokens.slice(0, 5).map((t, idx) => (
                      <div key={t.id || t.token_number} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.5rem 0.75rem', background:idx===0?C.tealLight:C.white, border:`1px solid ${idx===0?C.tealMid:C.border}`, borderRadius:'8px' }}>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:idx===0?'1.1rem':'0.95rem', color:idx===0?C.teal:C.muted, minWidth:'60px' }}>{t.token_number}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:'0.82rem', fontWeight:500, color:idx===0?C.text:C.muted }}>{t.patient_name}</div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontSize:'0.7rem', color:C.muted }}>~{getEstWait(idx)} min</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div style={{ padding:'0.75rem 1.5rem', borderBottom:`1px solid ${C.border}`, display:'flex', gap:'0.5rem', flexShrink:0, overflowX:'auto', background:C.white }}>
          {['ALL', 'WAITING', 'CALLED', 'IN_CONSULTATION', 'COMPLETED', 'SKIPPED'].map(status => {
            const def = QUEUE_STATUS[status];
            const count = status === 'ALL' ? queueData.length : (statusCounts[status] || 0);
            const isActive = filterStatus === status;
            return (
              <button key={status} onClick={() => setFilterStatus(status)}
                style={{ background: isActive ? (def?.bg || C.tealLight) : 'transparent', border:`1px solid ${isActive ? (def?.border || C.tealMid) : C.border}`, borderRadius:'20px', padding:'4px 12px', fontSize:'0.72rem', fontWeight:600, color: isActive ? (def?.color || C.teal) : C.muted, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0, transition:'all 0.2s' }}>
                {status === 'ALL' ? 'All' : (QUEUE_STATUS[status]?.label || status)} ({count})
              </button>
            );
          })}
        </div>

        {/* List */}
        <div style={{ flex:1, overflowY:'auto', padding:'1rem 1.5rem', background:C.bgInput }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'3rem', color:C.muted, fontSize:'0.85rem' }}>Loading queue…</div>
          ) : allFiltered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:C.mutedLight, fontSize:'0.85rem' }}>No patients in this category</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {allFiltered.map((patient, idx) => {
                const statusDef = QUEUE_STATUS[patient.status] || QUEUE_STATUS.WAITING;
                const isCurrent = patient.status === 'IN_CONSULTATION' || patient.status === 'CALLED';
                const waitIdx   = waitingTokens.findIndex(w => w.id === patient.id || w.token_number === patient.token_number);
                return (
                  <div key={patient.id || patient.token_number || idx} className="queue-row-enter"
                    style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'0.85rem 1rem', background: isCurrent ? statusDef.bg : C.white, border:`1px solid ${isCurrent ? statusDef.border : C.border}`, borderRadius:'10px', transition:'all 0.2s', animationDelay:`${idx * 0.03}s` }}>
                    <div style={{ width:'30px', height:'30px', borderRadius:'50%', background: isCurrent ? statusDef.bg : C.bgInput, border:`1px solid ${isCurrent ? statusDef.border : C.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'0.75rem', color: isCurrent ? statusDef.color : C.muted }}>{patient.queue_position || idx + 1}</span>
                    </div>
                    <div style={{ minWidth:'70px' }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'1rem', color: isCurrent ? statusDef.color : C.text }}>{patient.token_number}</div>
                      <div style={{ fontSize:'0.62rem', color:C.mutedLight }}>Token</div>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:'0.88rem', color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{patient.patient_name}</div>
                      <div style={{ fontSize:'0.72rem', color:C.muted }}>{patient.patient_id}</div>
                    </div>
                    {patient.doctor_name && (
                      <div style={{ textAlign:'right', flexShrink:0, minWidth:'100px' }}>
                        <div style={{ fontSize:'0.75rem', color:C.text, fontWeight:500 }}>{patient.doctor_name}</div>
                        <div style={{ fontSize:'0.62rem', color:C.mutedLight }}>Doctor</div>
                      </div>
                    )}
                    {patient.status === 'WAITING' && waitIdx >= 0 && (
                      <div style={{ textAlign:'right', flexShrink:0, minWidth:'60px' }}>
                        <div style={{ fontSize:'0.82rem', fontWeight:700, color:C.teal }}>~{getEstWait(waitIdx)}m</div>
                        <div style={{ fontSize:'0.62rem', color:C.mutedLight }}>est. wait</div>
                      </div>
                    )}
                    <div className={patient.status === 'CALLED' ? 'opd-called-pulse' : ''} style={{ flexShrink:0, display:'inline-flex', alignItems:'center', gap:'4px', padding:'4px 10px', borderRadius:'20px', fontSize:'0.7rem', fontWeight:600, background:statusDef.bg, color:statusDef.color, border:`1px solid ${statusDef.border}`, whiteSpace:'nowrap' }}>
                      {patient.status === 'WAITING' && '⏳'}{patient.status === 'CALLED' && '📢'}{patient.status === 'IN_CONSULTATION' && '🩺'}{patient.status === 'COMPLETED' && '✅'}{patient.status === 'SKIPPED' && '⏭'}
                      {' '}{statusDef.label}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding:'0.75rem 1.5rem', borderTop:`1px solid ${C.border}`, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between', background:C.white }}>
          <span style={{ fontSize:'0.72rem', color:C.mutedLight }}>Auto-refreshes every 10 seconds</span>
          <button className="opd-btn-primary" style={{ ...S.btnPrimary, width:'auto', padding:'7px 20px', fontSize:'0.8rem' }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
/*  WAITING ROOM PANEL                                                          */
/* ════════════════════════════════════════════════════════════════════════════ */
const WaitingRoomPanel = ({ departments, avgConsultMins = 10 }) => {
  const [waitingData, setWaitingData]   = useState({});
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const pollRef = useRef(null);

  const fetchAllQueues = useCallback(async () => {
    if (!departments.length) return;
    const deptIds = departments.map(d => d.id);
    const results = {};
    await Promise.allSettled(deptIds.map(async (id) => {
      try {
        const data = await receptionAPI.getTokens({ department_id: id });
        results[id] = (Array.isArray(data) ? data : data?.results || []).map(normalizeToken);
      } catch { results[id] = []; }
    }));
    setWaitingData(results);
    if (!selectedDeptId && deptIds.length > 0) {
      setSelectedDeptId(deptIds.find(id => (results[id] || []).length > 0) || deptIds[0]);
    }
  }, [departments, selectedDeptId]);

  useEffect(() => {
    fetchAllQueues();
    pollRef.current = setInterval(fetchAllQueues, 15_000);
    return () => clearInterval(pollRef.current);
  }, [fetchAllQueues]);

  const activeDepts   = departments.filter(d => (waitingData[d.id] || []).length > 0);
  const currentDept   = departments.find(d => String(d.id) === String(selectedDeptId));
  const queue         = waitingData[selectedDeptId] || [];
  const nowServing    = queue.find(q => q.status === 'IN_CONSULTATION' || q.status === 'CALLED');
  const waitingTokens = queue.filter(q => q.status === 'WAITING');
  const nextTokens    = waitingTokens.slice(0, 5);
  const getEstWait    = (idx) => Math.round(idx * avgConsultMins + (nowServing ? avgConsultMins / 2 : avgConsultMins));

  if (activeDepts.length === 0 && Object.keys(waitingData).length === 0) return null;

  return (
    <div style={S.card} className="opd-fade-in">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
        <div style={S.sectionLabel}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          Waiting Room
        </div>
        <span style={S.live}><span style={S.liveDot}/>LIVE</span>
      </div>

      {activeDepts.length > 1 && (
        <div style={{ display:'flex', gap:'0.4rem', marginBottom:'1rem', flexWrap:'wrap' }}>
          {activeDepts.map(d => {
            const waiting = (waitingData[d.id] || []).filter(x => x.status === 'WAITING').length;
            const isActive = String(d.id) === String(selectedDeptId);
            return (
              <button key={d.id} onClick={() => setSelectedDeptId(d.id)}
                style={{ background: isActive ? C.tealLight : 'transparent', border:`1px solid ${isActive ? C.tealMid : C.border}`, borderRadius:'20px', padding:'4px 12px', fontSize:'0.72rem', fontWeight:600, color: isActive ? C.teal : C.muted, cursor:'pointer', transition:'all 0.2s' }}>
                {d.name} <span style={{ opacity:0.7 }}>({waiting})</span>
              </button>
            );
          })}
        </div>
      )}

      {currentDept && (
        <>
          <div style={{ display:'grid', gridTemplateColumns: nowServing ? '1fr 1fr' : '1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
            {nowServing ? (
              <div style={{ background: nowServing.status==='CALLED' ? C.yellowLight : C.greenLight, border:`1px solid ${nowServing.status==='CALLED' ? '#fde68a' : '#a7f3d0'}`, borderRadius:'12px', padding:'1rem', textAlign:'center' }}>
                <div style={{ fontSize:'0.62rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color: nowServing.status==='CALLED' ? C.yellow : C.green, marginBottom:'4px' }}>
                  {nowServing.status === 'CALLED' ? '📢 Called' : '🩺 Now Serving'}
                </div>
                <div className={nowServing.status==='IN_CONSULTATION' ? 'opd-now-serving' : 'opd-called-pulse'} style={{ fontFamily:"'Syne',sans-serif", fontSize:'2.8rem', fontWeight:800, color: nowServing.status==='CALLED' ? C.yellow : C.green, lineHeight:1 }}>
                  {nowServing.token_number}
                </div>
                <div style={{ fontSize:'0.78rem', color:C.textMid, marginTop:'6px', fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{nowServing.patient_name}</div>
              </div>
            ) : (
              <div style={{ background:C.bgInput, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'1rem', textAlign:'center' }}>
                <div style={{ fontSize:'0.62rem', color:C.mutedLight, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px' }}>Now Serving</div>
                <div style={{ fontSize:'1rem', color:C.mutedLight }}>—</div>
                <div style={{ fontSize:'0.72rem', color:C.mutedLight, marginTop:'4px' }}>No active consultation</div>
              </div>
            )}
            <div style={{ background:C.bgInput, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'1rem' }}>
              <div style={{ fontSize:'0.62rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'6px' }}>Queue Status</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.4rem' }}>
                {[{ label:'Waiting', val: waitingTokens.length, color:C.teal },{ label:'In Consult', val: queue.filter(q=>q.status==='IN_CONSULTATION').length, color:C.green },{ label:'Completed', val: queue.filter(q=>q.status==='COMPLETED').length, color:C.muted },{ label:'Est. Wait', val: waitingTokens.length > 0 ? `~${getEstWait(waitingTokens.length - 1)}m` : '—', color:C.yellow }].map(({ label, val, color }) => (
                  <div key={label} style={{ textAlign:'center', padding:'0.4rem', background:C.white, borderRadius:'6px', border:`1px solid ${C.border}` }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'1.2rem', fontWeight:800, color }}>{val}</div>
                    <div style={{ fontSize:'0.62rem', color:C.muted }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {nextTokens.length > 0 && (
            <>
              <div style={{ fontSize:'0.65rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                Next Up
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
                {nextTokens.map((t, idx) => (
                  <div key={t.id || t.token_number || idx} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.55rem 0.75rem', background: idx===0 ? C.tealLight : C.bgInput, border:`1px solid ${idx===0 ? C.tealMid : C.border}`, borderRadius:'8px' }}>
                    <div style={{ width:'24px', height:'24px', borderRadius:'50%', background: idx===0 ? C.teal : C.border, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'0.65rem', color: idx===0 ? C.white : C.muted }}>{idx + 1}</span>
                    </div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'0.9rem', color: idx===0 ? C.teal : C.textMid, minWidth:'56px' }}>{t.token_number}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'0.8rem', fontWeight:500, color: idx===0 ? C.text : C.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.patient_name}</div>
                    </div>
                    <div style={{ fontSize:'0.72rem', fontWeight:600, color: idx===0 ? C.teal : C.muted }}>~{getEstWait(idx)} min</div>
                    <div style={{ fontSize:'0.65rem', padding:'2px 8px', borderRadius:'12px', background:QUEUE_STATUS.WAITING.bg, color:QUEUE_STATUS.WAITING.color, border:`1px solid ${QUEUE_STATUS.WAITING.border}`, whiteSpace:'nowrap', flexShrink:0 }}>Waiting</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {queue.length === 0 && (
            <div style={{ textAlign:'center', padding:'1.5rem', color:C.mutedLight, fontSize:'0.82rem' }}>No patients in queue for {currentDept?.name}</div>
          )}
        </>
      )}

      {activeDepts.length === 0 && (
        <div style={{ textAlign:'center', padding:'1.5rem', color:C.mutedLight, fontSize:'0.82rem' }}>No active waiting rooms at this time</div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
/*  MY TOKEN TRACKER                                                            */
/* ════════════════════════════════════════════════════════════════════════════ */
const MyTokenTracker = ({ token, deptId, avgConsultMins = 10 }) => {
  const [liveStatus, setLiveStatus]     = useState(null);
  const [queuePosition, setQueuePosition] = useState(token?.queue_position);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!token?.id && !token?.token_number) return;
    try {
      if (deptId) {
        const data = await receptionAPI.getTokens({ department_id: deptId });
        const list = (Array.isArray(data) ? data : data?.results || []).map(normalizeToken);
        const myEntry = list.find(q => q.token_number === token.token_number || q.id === token.id);
        if (myEntry) {
          setLiveStatus(myEntry.status || 'WAITING');
          setQueuePosition(list.filter(q => q.status === 'WAITING' && (q.queue_position || 0) < (myEntry.queue_position || 999)).length + 1);
        }
      }
    } catch {}
  }, [token, deptId]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 12_000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const statusDef = QUEUE_STATUS[liveStatus] || QUEUE_STATUS.WAITING;
  const ahead     = Math.max(0, (queuePosition || 1) - 1);
  const estWait   = Math.round(ahead * avgConsultMins + (ahead > 0 ? avgConsultMins / 2 : 0));

  return (
    <div style={{ background: statusDef.bg, border:`1.5px solid ${statusDef.border}`, borderRadius:'12px', padding:'1rem', marginTop:'1rem' }} className="opd-fade-in">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem' }}>
        <div style={{ fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:statusDef.color }}>Live Token Status</div>
        <span style={S.live}><span style={S.liveDot}/>Auto-updating</span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.6rem' }}>
        {[{ label:'Token', val:token?.token_number, color:statusDef.color },{ label:'Position', val:queuePosition||'—', color:ahead===0?C.green:C.text },{ label:'Est. Wait', val:estWait>0?`~${estWait}m`:'—', color:estWait<=10?C.green:C.yellow }].map(({ label, val, color }) => (
          <div key={label} style={{ textAlign:'center', padding:'0.6rem', background:'rgba(255,255,255,0.6)', borderRadius:'8px', border:`1px solid ${statusDef.border}` }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'1.4rem', fontWeight:800, color }}>{val}</div>
            <div style={{ fontSize:'0.62rem', color:C.muted, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:'0.75rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
        <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:statusDef.color, flexShrink:0 }}/>
        <span style={{ fontSize:'0.8rem', fontWeight:600, color:statusDef.color }}>{statusDef.label}</span>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
/*  TODAY'S QUEUE PANEL                                                         */
/* ════════════════════════════════════════════════════════════════════════════ */
const TodayQueuePanel = ({ departments }) => {
  const [tokens, setTokens]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [updating, setUpdating]   = useState(null);
  const [filterDept, setFilterDept]     = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const pollRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await receptionAPI.getTokens({});
      setTokens((Array.isArray(data) ? data : data?.results || []).map(normalizeToken));
    } catch { setTokens([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    pollRef.current = setInterval(fetchAll, 15_000);
    return () => clearInterval(pollRef.current);
  }, [fetchAll]);

  const updateStatus = async (tokenId, newBackendStatus) => {
    setUpdating(tokenId);
    try {
      await receptionAPI.updateTokenStatus(tokenId, newBackendStatus);
      setTokens(prev => prev.map(t => t.id === tokenId ? { ...t, status: BACKEND_STATUS_MAP[newBackendStatus] || newBackendStatus } : t));
      setTimeout(fetchAll, 800);
    } catch(e) { notify.error(e?.response?.data?.error || 'Status update failed'); }
    finally { setUpdating(null); }
  };

  const NEXT_STATUS = {
    WAITING:     [{ label:'📢 Call', value:'IN_PROGRESS', color:C.yellow }, { label:'⏭ Skip', value:'CANCELLED', color:C.red }],
    IN_PROGRESS: [{ label:'✅ Complete', value:'COMPLETED', color:C.green }, { label:'↩ Revert', value:'WAITING', color:C.teal }],
    COMPLETED: [], CANCELLED: [], NO_SHOW: [],
  };
  const toBackend = { WAITING:'WAITING', IN_CONSULTATION:'IN_PROGRESS', CALLED:'IN_PROGRESS', COMPLETED:'COMPLETED', SKIPPED:'CANCELLED' };

  const deptIds     = [...new Set(tokens.map(t => t.department_id).filter(Boolean))];
  const deptOptions = departments.filter(d => deptIds.includes(String(d.id)) || deptIds.includes(d.id));
  const filtered    = tokens.filter(t => {
    const deptMatch   = filterDept === 'ALL' || (t.department_id && String(t.department_id) === String(filterDept));
    const statMatch   = filterStatus === 'ALL' || ['WAITING','IN_CONSULTATION','CALLED'].includes(t.status);
    return deptMatch && statMatch;
  });
  const counts = tokens.reduce((a, t) => { a[t.status] = (a[t.status]||0)+1; return a; }, {});

  return (
    <div style={S.card}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.85rem' }}>
        <div style={S.sectionLabel}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Today's Tokens
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span style={S.live}><span style={S.liveDot}/>LIVE</span>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.4rem', marginBottom:'0.85rem' }}>
        {[{ label:'Waiting', count:counts.WAITING||0, color:C.teal },{ label:'Called', count:(counts.IN_CONSULTATION||0)+(counts.CALLED||0), color:C.yellow },{ label:'Done', count:counts.COMPLETED||0, color:C.green },{ label:'Skipped', count:(counts.SKIPPED||0)+(counts.CANCELLED||0), color:C.red }].map(({ label, count, color }) => (
          <div key={label} style={{ textAlign:'center', padding:'0.45rem 0', background:C.bgInput, borderRadius:'8px', border:`1px solid ${C.border}` }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'1.2rem', fontWeight:800, color }}>{count}</div>
            <div style={{ fontSize:'0.6rem', color:C.muted }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:'0.4rem', marginBottom:'0.75rem', flexWrap:'wrap' }}>
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
          style={{ ...S.select, fontSize:'0.75rem', padding:'5px 8px', flex:1, minWidth:'100px' }}>
          <option value="ALL">All Departments</option>
          {deptOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button onClick={() => setFilterStatus(s => s === 'ALL' ? 'ACTIVE' : 'ALL')}
          style={{ ...S.btnSecondary, fontSize:'0.72rem', padding:'5px 10px', whiteSpace:'nowrap' }}>
          {filterStatus === 'ACTIVE' ? 'Show All' : 'Active Only'}
        </button>
      </div>

      {loading && tokens.length === 0 ? (
        <div style={{ textAlign:'center', padding:'1.5rem', color:C.muted, fontSize:'0.82rem' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'1.5rem', color:C.muted, fontSize:'0.82rem' }}>
          {filterStatus === 'ACTIVE' ? 'No active tokens' : 'No tokens today'}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', maxHeight:'320px', overflowY:'auto', paddingRight:'2px' }}>
          {filtered.map(t => {
            const def = QUEUE_STATUS[t.status] || QUEUE_STATUS.WAITING;
            const nextActions = NEXT_STATUS[toBackend[t.status] || t.status] || [];
            const isUpdating = updating === t.id;
            return (
              <div key={t.id || t.token_number}
                style={{ padding:'0.65rem 0.85rem', background: def.bg, border:`1px solid ${def.border}`, borderRadius:'10px', transition:'all 0.2s' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'0.92rem', color:def.color, minWidth:'58px', flexShrink:0 }}>
                    {t.token_number}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'0.82rem', fontWeight:600, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.patient_name}</div>
                    <div style={{ fontSize:'0.68rem', color:C.muted }}>{t.patient_id}</div>
                  </div>
                  <div style={{ fontSize:'0.65rem', fontWeight:700, color:def.color, flexShrink:0 }}>{def.label}</div>
                </div>
                {nextActions.length > 0 && (
                  <div style={{ display:'flex', gap:'0.4rem', marginTop:'0.45rem' }}>
                    {nextActions.map(({ label, value, color }) => (
                      <button key={value} disabled={isUpdating}
                        onClick={() => updateStatus(t.id, value)}
                        style={{ flex:1, padding:'4px 6px', borderRadius:'6px', border:`1.5px solid ${color}44`, background:`${color}18`, color, fontSize:'0.7rem', fontWeight:700, cursor:isUpdating?'wait':'pointer', fontFamily:"'DM Sans',sans-serif", opacity:isUpdating?0.6:1, transition:'all 0.15s' }}>
                        {isUpdating ? '…' : label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                              */
/* ════════════════════════════════════════════════════════════════════════════ */
const Recepsonist = () => {
  const { user, logout } = useAuth();

  useEffect(() => { injectFonts(); injectKeyframes(); }, []);

  const [step, setStep]                       = useState(1);
  const [searchQuery, setSearchQuery]         = useState('');
  const [searchResults, setSearchResults]     = useState([]);
  const [searching, setSearching]             = useState(false);
  const [noResults, setNoResults]             = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showRegisterForm, setShowRegisterForm] = useState(false);

  const [regForm, setRegForm] = useState({
    first_name:'', last_name:'', dob:'', age:'', gender:'', blood_group:'',
    phone:'', email:'', address:'',
    aadhar_number:'', abha_number:'',
    emergency_name:'', emergency_relation:'', emergency_phone:'',
    allergies:'', current_medications:'', previous_conditions:'',
  });
  const [regErrors, setRegErrors]     = useState({});
  const [registering, setRegistering] = useState(false);
  const [regError, setRegError]       = useState('');

  const [departments, setDepartments]         = useState([]);
  const [selectedDept, setSelectedDept]       = useState('');
  const [doctors, setDoctors]                 = useState([]);
  const [loadingDoctors, setLoadingDoctors]   = useState(false);
  const [selectedDoctor, setSelectedDoctor]   = useState(null);

  const [token, setToken]                     = useState(null);
  const [opdRecord, setOpdRecord]             = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [tokenError, setTokenError]           = useState('');
  const [printing, setPrinting]               = useState(false);
  const [cardCopies, setCardCopies]           = useState(1);
  const [showScanModal, setShowScanModal]     = useState(false);

  const [queueOverview, setQueueOverview]     = useState([]);
  const [queueLoading, setQueueLoading]       = useState(false);
  const [queueModalDept, setQueueModalDept]   = useState(null);

  const queueIntervalRef = useRef(null);
  const debounceRef      = useRef(null);

  useEffect(() => {
    loadDepartments();
    loadQueueOverview();
    queueIntervalRef.current = setInterval(loadQueueOverview, 20_000);
    return () => clearInterval(queueIntervalRef.current);
  }, []);

  useEffect(() => {
    if (selectedDept) loadDoctors(selectedDept);
    else setDoctors([]);
    setSelectedDoctor(null);
  }, [selectedDept]);

  const loadDepartments = async () => {
    try {
      const data = await departmentAPI.getDepartments();
      setDepartments((Array.isArray(data) ? data : []).filter(d => CLINICAL_DEPT_CODES.has(d.code)));
    } catch {}
  };

  const loadDoctors = async (deptId) => {
    setLoadingDoctors(true);
    try {
      const data = await receptionAPI.getDoctorsByDepartment(deptId);
      setDoctors(Array.isArray(data) ? data : data?.results || []);
    } catch { setDoctors([]); }
    finally  { setLoadingDoctors(false); }
  };

  const loadQueueOverview = async () => {
    setQueueLoading(true);
    try {
      const data = await receptionAPI.getQueueOverview();
      setQueueOverview(Array.isArray(data) ? data : data?.results || []);
    } catch {} finally { setQueueLoading(false); }
  };

  const generalDepts = departments.filter(d => GENERAL_DEPT_CODES.includes(d.code));
  const superDepts   = departments.filter(d => SUPER_SPECIALTY_CODES.includes(d.code));

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setNoResults(false);
    setSearchResults([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) return;
    debounceRef.current = setTimeout(() => runSearch(q.trim()), 350);
  };

  const runSearch = async (q) => {
    setSearching(true);
    try {
      const data    = await receptionAPI.searchPatient(q);
      const results = Array.isArray(data) ? data : data?.results || [];
      setSearchResults(results);
      setNoResults(results.length === 0);
    } catch { setSearchResults([]); setNoResults(true); }
    finally  { setSearching(false); }
  };

  const selectPatient = (p) => {
    setSelectedPatient(p);
    setSearchResults([]);
    setSearchQuery('');
    setShowRegisterForm(false);
    setStep(2);
  };

  const validateRegForm = () => {
    const errs = {};
    const aErr = validateAadhar(regForm.aadhar_number);
    const bErr = validateABHA(regForm.abha_number);
    if (aErr) errs.aadhar_number = aErr;
    if (bErr) errs.abha_number   = bErr;
    if (!regForm.phone)      errs.phone      = 'Phone is required';
    if (!regForm.first_name) errs.first_name = 'Required';
    if (!regForm.last_name)  errs.last_name  = 'Required';
    return errs;
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    const errs = validateRegForm();
    setRegErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setRegistering(true);
    setRegError('');
    try {
      const payload = {
        first_name: regForm.first_name,
        last_name:  regForm.last_name,
        date_of_birth: regForm.dob || null,
        age: regForm.age ? parseInt(regForm.age) : null,
        gender: regForm.gender,
        blood_group: regForm.blood_group,
        phone: regForm.phone,
        email: regForm.email,
        address: regForm.address,
        aadhar_number: regForm.aadhar_number.replace(/[-\s]/g, ''),
        abha_number: regForm.abha_number
          ? regForm.abha_number.replace(/[-\s]/g, '').replace(/(\d{2})(\d{4})(\d{4})(\d{4})/, '$1-$2-$3-$4')
          : '',
        emergency_contact_name:     regForm.emergency_name,
        emergency_contact_relation: regForm.emergency_relation,
        emergency_contact_phone:    regForm.emergency_phone,
        allergies:           regForm.allergies,
        current_medications: regForm.current_medications,
        previous_conditions: regForm.previous_conditions,
      };
      const newPatient = await receptionAPI.registerPatient(payload);
      selectPatient(newPatient);
    } catch (err) {
      setRegError(err?.response?.data?.error || JSON.stringify(err?.response?.data) || 'Registration failed.');
    } finally {
      setRegistering(false);
    }
  };

  /* ── Generate token — chief_complaint removed, no longer required ── */
  const generateToken = async () => {
    if (!selectedPatient || !selectedDoctor) return;
    setGeneratingToken(true);
    setTokenError('');
    try {
      const resp = await receptionAPI.generateToken({
        patient_id:    selectedPatient.id,
        doctor_id:     selectedDoctor.id,
        department_id: parseInt(selectedDept),
      });
      const tokenData = resp.token || resp;
      const opdData   = resp.opd_record || null;
      setToken(tokenData);
      setOpdRecord(opdData);
      loadQueueOverview();

      const _deptName   = departments.find(d => String(d.id) === String(selectedDept))?.name || '';
      const _doctorName = selectedDoctor ? `Dr. ${selectedDoctor.last_name || selectedDoctor.full_name || ''}` : '';

      setPrinting(true);
      await printOPDSlip(opdData, tokenData, _deptName, _doctorName, selectedPatient);
      setPrinting(false);
      setStep(3);
    } catch (err) {
      setTokenError(err?.response?.data?.error || 'Failed to generate token.');
    } finally {
      setGeneratingToken(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setSearchQuery(''); setSearchResults([]); setNoResults(false);
    setSelectedPatient(null); setShowRegisterForm(false);
    setRegForm({ first_name:'', last_name:'', dob:'', age:'', gender:'', blood_group:'', phone:'', email:'', address:'', aadhar_number:'', abha_number:'', emergency_name:'', emergency_relation:'', emergency_phone:'', allergies:'', current_medications:'', previous_conditions:'' });
    setRegErrors({});
    setSelectedDept(''); setDoctors([]); setSelectedDoctor(null);
    setToken(null); setOpdRecord(null); setTokenError('');
    setPrinting(false);
  };

  const statusLabel = (s) => s==='AVAILABLE'?'Available':s==='IN_CONSULTATION'?'In Consultation':s==='ON_LEAVE'?'On Leave':'Off Duty';
  const statusColor = (s) => s==='AVAILABLE'?'green':s==='IN_CONSULTATION'?'yellow':'red';
  const waitMins    = (doc) => (doc?.current_queue_count||0) * (doc?.avg_consultation_mins||10);

  const deptName   = departments.find(d => String(d.id) === String(selectedDept))?.name || '';
  const doctorName = selectedDoctor ? `Dr. ${selectedDoctor.last_name || selectedDoctor.full_name || ''}` : '';

  const generateBtnLabel = () => {
    if (generatingToken) return 'Generating Token…';
    if (printing) return 'Waiting for print…';
    return `Generate & Print OPD Token — Dr. ${selectedDoctor?.last_name || selectedDoctor?.full_name}`;
  };

  const findDeptForQueueItem = (q) => {
    return departments.find(d =>
      d.name === (q.department_name || q.department) ||
      String(d.id) === String(q.department_id)
    ) || { id: q.department_id, name: q.department_name || q.department };
  };

  return (
    <div style={S.root}>

      {/* ── Top bar ── */}
      <header style={S.topbar}>
        <div style={S.logo}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          OPD Reception
        </div>
        <div style={S.topbarRight}>
          {/* Step indicator */}
          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            {[1,2,3].map(n => (
              <React.Fragment key={n}>
                <div style={S.stepBadge(step >= n)}>{n}</div>
                {n < 3 && <div style={{ width:'20px', height:'2px', background:step > n ? C.teal : C.border, borderRadius:'2px' }}/>}
              </React.Fragment>
            ))}
            <span style={{ fontSize:'0.75rem', color:C.muted, marginLeft:'6px' }}>
              {step===1 ? 'Patient' : step===2 ? 'Department & Doctor' : 'OPD Slip'}
            </span>
          </div>

          {/* Scan card button */}
          <button onClick={() => setShowScanModal(true)}
            style={{ display:'flex', alignItems:'center', gap:'6px', background:C.tealLight, border:`1px solid ${C.tealMid}`, borderRadius:'8px', color:C.teal, padding:'6px 14px', fontSize:'0.78rem', fontWeight:600, cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="7" width="3" height="10"/><rect x="7" y="5" width="2" height="14"/><rect x="11" y="7" width="1" height="10"/><rect x="14" y="5" width="3" height="14"/><rect x="19" y="7" width="2" height="10"/></svg>
            Scan Card
          </button>

          <div style={S.userTag}>{user?.first_name || user?.username} · Receptionist</div>
          <button className="opd-logout-btn" style={S.logoutBtn} onClick={logout}>Sign out</button>
        </div>
      </header>

      <div style={S.layout}>

        {/* ═══════════ LEFT SIDEBAR ═══════════ */}
        <aside style={S.sidebar}>

          {/* Step 1 — Patient search / register */}
          <div style={S.card} className="opd-fade-in">
            <div style={S.sectionLabel}>
              <div style={S.stepBadge(true)}>1</div>
              Patient
            </div>

            {selectedPatient && (
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', background:C.tealLight, border:`1.5px solid ${C.tealMid}`, borderRadius:'10px', padding:'0.75rem', marginBottom:'0.75rem' }}>
                <div style={S.patientAvatar}>{selectedPatient.first_name?.[0]}{selectedPatient.last_name?.[0]}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:'0.92rem', color:C.text }}>{selectedPatient.first_name} {selectedPatient.last_name}</div>
                  <div style={{ fontSize:'0.75rem', color:C.muted }}>{selectedPatient.patient_id} · {selectedPatient.phone}</div>
                </div>
                <button className="opd-btn-secondary" style={S.btnSecondary}
                  onClick={() => { setSelectedPatient(null); setStep(1); }}>Change</button>
              </div>
            )}

            {!selectedPatient && !showRegisterForm && (
              <>
                <div style={S.formGroup}>
                  <label style={S.label}>Search by name, phone, ID or Aadhar</label>
                  <input className="opd-input" style={S.input}
                    placeholder="e.g. Ramesh, 9876543210, PAT000042"
                    value={searchQuery} onChange={handleSearchChange} />
                </div>
                {searching && <div style={{ textAlign:'center', padding:'1rem', color:C.muted, fontSize:'0.82rem' }}>Searching…</div>}
                {searchResults.map(p => (
                  <div key={p.id} className="opd-patient-result" style={S.patientResult} onClick={() => selectPatient(p)}>
                    <div style={S.patientAvatar}>{p.first_name?.[0]}{p.last_name?.[0]}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:'0.88rem', color:C.text }}>{p.first_name} {p.last_name}</div>
                      <div style={{ fontSize:'0.74rem', color:C.muted }}>{p.patient_id} · {p.phone}{p.blood_group && ` · ${p.blood_group}`}</div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                  </div>
                ))}
                {noResults && searchQuery.length >= 2 && (
                  <div style={{ textAlign:'center', padding:'1rem 0' }}>
                    <div style={{ fontSize:'0.82rem', color:C.muted, marginBottom:'0.75rem' }}>No patient found for "{searchQuery}"</div>
                    <button className="opd-btn-primary" style={S.btnPrimary} onClick={() => setShowRegisterForm(true)}>+ Register New Patient</button>
                  </div>
                )}
                {!searchQuery && (
                  <div style={{ marginTop:'0.5rem' }}>
                    <button className="opd-btn-secondary" style={{ ...S.btnSecondary, width:'100%' }} onClick={() => setShowRegisterForm(true)}>+ New Patient Registration</button>
                  </div>
                )}
              </>
            )}

            {/* ── Registration Form ── */}
            {showRegisterForm && !selectedPatient && (
              <form onSubmit={handleRegSubmit} className="opd-fade-in">
                {regError && <div style={S.errorBox}>{regError}</div>}

                <div className="section-divider">Basic Info</div>
                <div style={S.formGrid2}>
                  {[['First Name','first_name',true],['Last Name','last_name',true]].map(([lbl,k,req]) => (
                    <div key={k} style={S.formGroup}>
                      <label style={S.label}>{lbl}{req ? ' *' : ''}</label>
                      <input className="opd-input" style={{ ...S.input, borderColor:regErrors[k]?C.red:undefined }} required={req}
                        value={regForm[k]} onChange={e => setRegForm({ ...regForm, [k]:e.target.value })} />
                      {regErrors[k] && <span style={{ fontSize:'0.7rem', color:C.red }}>{regErrors[k]}</span>}
                    </div>
                  ))}
                  <div style={S.formGroup}>
                    <label style={S.label}>Date of Birth</label>
                    <input type="date" className="opd-input" style={S.input} value={regForm.dob} onChange={e => setRegForm({ ...regForm, dob:e.target.value })} />
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Age</label>
                    <input type="number" className="opd-input" style={S.input} placeholder="years" value={regForm.age} onChange={e => setRegForm({ ...regForm, age:e.target.value })} />
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Gender *</label>
                    <select className="opd-input" style={S.select} required value={regForm.gender} onChange={e => setRegForm({ ...regForm, gender:e.target.value })}>
                      <option value="">Select</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="O">Other</option>
                    </select>
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Blood Group</label>
                    <select className="opd-input" style={S.select} value={regForm.blood_group} onChange={e => setRegForm({ ...regForm, blood_group:e.target.value })}>
                      <option value="">Select</option>
                      {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>

                <div className="section-divider">Contact</div>
                <div style={S.formGrid2}>
                  <div style={S.formGroup}>
                    <label style={S.label}>Phone *</label>
                    <input className="opd-input" style={{ ...S.input, borderColor:regErrors.phone?C.red:undefined }} required placeholder="+91XXXXXXXXXX"
                      value={regForm.phone} onChange={e => setRegForm({ ...regForm, phone:e.target.value })} />
                    {regErrors.phone && <span style={{ fontSize:'0.7rem', color:C.red }}>{regErrors.phone}</span>}
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Email</label>
                    <input type="email" className="opd-input" style={S.input} value={regForm.email} onChange={e => setRegForm({ ...regForm, email:e.target.value })} />
                  </div>
                </div>
                <div style={{ ...S.formGroup, marginTop:'0.5rem' }}>
                  <label style={S.label}>Address</label>
                  <input className="opd-input" style={S.input} value={regForm.address} onChange={e => setRegForm({ ...regForm, address:e.target.value })} />
                </div>

                <div className="section-divider">Government IDs</div>
                <div style={S.formGrid2}>
                  <div style={S.formGroup}>
                    <label style={S.label}>Aadhaar No</label>
                    <input className="opd-input" style={{ ...S.input, borderColor:regErrors.aadhar_number?C.red:undefined }} placeholder="XXXX-XXXX-XXXX"
                      value={regForm.aadhar_number} onChange={e => setRegForm({ ...regForm, aadhar_number:formatAadhar(e.target.value) })} maxLength={14} />
                    {regErrors.aadhar_number ? <span style={{ fontSize:'0.7rem', color:C.red }}>{regErrors.aadhar_number}</span> : <span style={S.fieldHint}>12-digit number</span>}
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>ABHA No</label>
                    <input className="opd-input" style={{ ...S.input, borderColor:regErrors.abha_number?C.red:undefined }} placeholder="XX-XXXX-XXXX-XXXX"
                      value={regForm.abha_number} onChange={e => setRegForm({ ...regForm, abha_number:formatABHA(e.target.value) })} maxLength={17} />
                    {regErrors.abha_number ? <span style={{ fontSize:'0.7rem', color:C.red }}>{regErrors.abha_number}</span> : <span style={S.fieldHint}>14-digit ABHA card</span>}
                  </div>
                </div>

                <div className="section-divider">Emergency Contact</div>
                <div style={S.formGrid3}>
                  {[['Name','emergency_name'],['Relation','emergency_relation'],['Phone','emergency_phone']].map(([lbl,k]) => (
                    <div key={k} style={S.formGroup}>
                      <label style={S.label}>{lbl}</label>
                      <input className="opd-input" style={S.input} value={regForm[k]} onChange={e => setRegForm({ ...regForm, [k]:e.target.value })} />
                    </div>
                  ))}
                </div>

                <div className="section-divider">Medical History</div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {[['Allergies','allergies','e.g. Penicillin, Pollen'],['Current Medications','current_medications',''],['Previous Conditions','previous_conditions','']].map(([lbl,k,ph]) => (
                    <div key={k} style={S.formGroup}>
                      <label style={S.label}>{lbl}</label>
                      <input className="opd-input" style={S.input} placeholder={ph} value={regForm[k]} onChange={e => setRegForm({ ...regForm, [k]:e.target.value })} />
                    </div>
                  ))}
                </div>

                <div style={{ display:'flex', gap:'0.6rem', marginTop:'1rem' }}>
                  <button type="button" className="opd-btn-secondary" style={{ ...S.btnSecondary, flex:1 }} onClick={() => setShowRegisterForm(false)}>Cancel</button>
                  <button type="submit" className="opd-btn-primary" style={{ ...S.btnPrimary, flex:2 }} disabled={registering}>
                    {registering ? 'Registering…' : 'Register & Continue'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Today's Tokens */}
          <TodayQueuePanel departments={departments} />

          {/* Live Queue Overview */}
          <div style={S.card}>
            <div style={{ ...S.sectionLabel, marginBottom:'0.6rem', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                <span style={S.live}><span style={S.liveDot}/>LIVE</span>
                Queue Overview
              </div>
              <span style={{ fontSize:'0.65rem', color:C.mutedLight, fontWeight:400, textTransform:'none', letterSpacing:0 }}>Click to view all</span>
            </div>

            {queueLoading && !queueOverview.length ? (
              <div style={{ color:C.muted, fontSize:'0.8rem', textAlign:'center', padding:'1rem' }}>Loading…</div>
            ) : queueOverview.length === 0 ? (
              <div style={{ color:C.mutedLight, fontSize:'0.8rem', textAlign:'center', padding:'1rem' }}>No active queues</div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem' }}>
                {queueOverview.map((q, i) => {
                  const deptObj   = findDeptForQueueItem(q);
                  const waiting   = q.waiting_count ?? q.total_queue ?? q.count ?? 0;
                  const inConsult = q.in_consultation_count ?? 0;
                  return (
                    <div key={i} className="opd-queue-dept-card"
                      onClick={() => setQueueModalDept(deptObj)}
                      style={{ background:C.bgInput, border:`1px solid ${C.border}`, borderRadius:'10px', padding:'0.7rem 0.85rem', transition:'all 0.2s', cursor:'pointer' }}>
                      <div style={{ fontSize:'0.7rem', color:C.muted, fontWeight:500, marginBottom:'4px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{q.department_name || q.department}</div>
                      <div style={{ display:'flex', alignItems:'baseline', gap:'4px' }}>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'1.5rem', color:C.teal }}>{waiting}</div>
                        <div style={{ fontSize:'0.65rem', color:C.muted }}>waiting</div>
                      </div>
                      {inConsult > 0 && (
                        <div style={{ display:'flex', alignItems:'center', gap:'4px', marginTop:'2px' }}>
                          <div style={{ width:'5px', height:'5px', borderRadius:'50%', background:C.green }}/>
                          <span style={{ fontSize:'0.65rem', color:C.green }}>{inConsult} in consult</span>
                        </div>
                      )}
                      <div style={{ fontSize:'0.62rem', color:C.mutedLight, marginTop:'3px', display:'flex', alignItems:'center', gap:'3px' }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        View all
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {/* ═══════════ RIGHT MAIN PANEL ═══════════ */}
        <main style={S.main}>

          {/* ── STEP 2: Department & Doctor ── */}
          {step >= 2 && step < 3 && (
            <div className="opd-fade-in">
              <div style={S.card}>
                <div style={S.sectionLabel}>
                  <div style={S.stepBadge(true)}>2</div>
                  Department &amp; Doctor
                </div>

                {/* Department selector */}
                <div style={{ ...S.formGroup, marginBottom:'1.25rem' }}>
                  <label style={S.label}>Select Department *</label>
                  <select className="opd-input" style={{ ...S.select, maxWidth:'420px' }}
                    value={selectedDept} onChange={e => setSelectedDept(e.target.value)}>
                    <option value="">Choose department…</option>
                    {generalDepts.length > 0 && (
                      <optgroup label="── General / Core Clinical ──">
                        {generalDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </optgroup>
                    )}
                    {superDepts.length > 0 && (
                      <optgroup label="── Super-Specialty ──">
                        {superDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </optgroup>
                    )}
                  </select>
                </div>

                {loadingDoctors && (
                  <div style={{ color:C.muted, fontSize:'0.85rem', textAlign:'center', padding:'2rem' }}>Loading doctors…</div>
                )}
                {!loadingDoctors && selectedDept && doctors.length === 0 && (
                  <div style={{ color:C.muted, fontSize:'0.85rem', textAlign:'center', padding:'2rem' }}>No doctors found for this department.</div>
                )}

                {/* Doctor cards */}
                {doctors.map(doc => {
                  const disabled = doc.availability_status === 'ON_LEAVE' || doc.availability_status === 'OFF_DUTY';
                  const selected = selectedDoctor?.id === doc.id;
                  return (
                    <div key={doc.id} className={`opd-doctor-card${disabled ? ' disabled' : ''}`}
                      style={S.doctorCard(selected, disabled)}
                      onClick={() => !disabled && setSelectedDoctor(doc)}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'1rem' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'4px' }}>
                            <div style={S.statusDot(doc.availability_status)} />
                            <span style={{ fontWeight:700, fontSize:'0.95rem', color:C.text }}>{doc.full_name || `${doc.first_name} ${doc.last_name}`}</span>
                            {selected && <span style={S.badge('cyan')}>✓ Selected</span>}
                          </div>
                          <div style={{ fontSize:'0.78rem', color:C.muted }}>
                            {doc.qualification && <span>{doc.qualification} · </span>}
                            {doc.designation || doc.faculty_subrole_display}
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={S.badge(statusColor(doc.availability_status))}>{statusLabel(doc.availability_status)}</div>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:'1.5rem', marginTop:'0.75rem' }}>
                        <div>
                          <div style={{ fontSize:'0.68rem', color:C.mutedLight, marginBottom:'2px' }}>Queue</div>
                          <div style={{ fontWeight:700, fontSize:'1rem', color:doc.current_queue_count > 10 ? C.yellow : C.text }}>{doc.current_queue_count ?? 0}</div>
                        </div>
                        <div>
                          <div style={{ fontSize:'0.68rem', color:C.mutedLight, marginBottom:'2px' }}>Est. Wait</div>
                          <div style={{ fontWeight:700, fontSize:'1rem', color:C.textMid }}>{waitMins(doc)} min</div>
                        </div>
                        {doc.consultation_fee && (
                          <div>
                            <div style={{ fontSize:'0.68rem', color:C.mutedLight, marginBottom:'2px' }}>Fee</div>
                            <div style={{ fontWeight:700, fontSize:'1rem', color:C.teal }}>₹{doc.consultation_fee}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Generate token button */}
                {selectedDoctor && (
                  <div style={{ marginTop:'1rem' }}>
                    {tokenError && <div style={S.errorBox}>{tokenError}</div>}
                    {printing && (
                      <div style={{ background:C.tealLight, border:`1px solid ${C.tealMid}`, borderRadius:'9px', padding:'10px 14px', fontSize:'0.83rem', color:C.teal, marginBottom:'0.75rem', display:'flex', alignItems:'center', gap:'8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                        </svg>
                        Print dialog is open — complete printing or save as PDF to continue…
                      </div>
                    )}
                    <button className="opd-btn-primary"
                      style={{ ...S.btnPrimary, opacity:(generatingToken || printing) ? 0.55 : 1 }}
                      onClick={generateToken}
                      disabled={generatingToken || printing}>
                      <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                        {!generatingToken && !printing && (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                          </svg>
                        )}
                        {generateBtnLabel()}
                      </span>
                    </button>
                  </div>
                )}
              </div>

              <PatientSummaryCard patient={selectedPatient} S={S} C={C} />
            </div>
          )}

          {/* ── STEP 3: Token success ── */}
          {step === 3 && token && (
            <div className="opd-fade-in">
              <div style={S.tokenCard} className="opd-token-anim">
                <div style={{ position:'relative' }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:700, color:'rgba(255,255,255,0.7)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'0.5rem' }}>OPD Token Generated</div>
                  <div style={S.tokenNumber}>{token.token_number}</div>
                  <div style={{ fontSize:'0.82rem', color:'rgba(255,255,255,0.75)', marginTop:'0.5rem' }}>
                    Queue position: <strong style={{ color:C.white }}>#{token.queue_position}</strong>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginTop:'1.5rem', textAlign:'left' }}>
                    <InfoBlockLight label="Patient"     value={opdRecord?.patient_name_snapshot || `${selectedPatient?.first_name} ${selectedPatient?.last_name}`} />
                    <InfoBlockLight label="Patient ID"  value={token.patient_id_num || selectedPatient?.patient_id} />
                    <InfoBlockLight label="Doctor"      value={opdRecord?.doctor_name || doctorName} />
                    <InfoBlockLight label="Department"  value={opdRecord?.department_name || deptName} />
                    <InfoBlockLight label="Date & Time" value={opdRecord?.visit_datetime || new Date().toLocaleString('en-IN')} />
                    <InfoBlockLight label="Est. Wait"   value={`${waitMins(selectedDoctor)} min`} />
                  </div>
                  <div style={{ marginTop:'1.25rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', fontSize:'0.78rem', color:'rgba(255,255,255,0.9)', fontWeight:600 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Slip printed / saved
                  </div>
                </div>
              </div>

              {/* Live tracker */}
              <MyTokenTracker token={token} deptId={selectedDept} avgConsultMins={selectedDoctor?.avg_consultation_mins || 10} />

              {/* Action buttons */}
              <div style={{ display:'flex', gap:'0.75rem', marginTop:'0.5rem' }}>
                <button className="opd-btn-secondary" style={{ ...S.btnSecondary, flex:1, padding:'10px 20px' }}
                  onClick={() => printOPDSlip(opdRecord, token, deptName, doctorName, selectedPatient)}>
                  <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                    </svg>
                    Reprint OPD Slip
                  </span>
                </button>
                <button className="opd-btn-primary" style={{ ...S.btnPrimary, flex:2 }} onClick={resetAll}>
                  ✓ Done — Next Patient
                </button>
              </div>

              {/* Patient card copies row */}
              <div style={{ marginTop:'0.6rem', display:'flex', gap:'0.5rem', alignItems:'center', background:C.tealLight, border:`1px solid ${C.tealMid}`, borderRadius:'12px', padding:'0.65rem 0.9rem' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="2" strokeLinecap="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                <span style={{ fontSize:'0.8rem', color:C.textMid, flex:1, fontWeight:500 }}>Patient ID Card copies</span>
                <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                  {[['−', () => setCardCopies(c => Math.max(1, c-1))], ['+', () => setCardCopies(c => Math.min(20, c+1))]].map(([lbl, fn], i) => (
                    i === 0 ? (
                      <button key={lbl} onClick={fn} style={{ width:26, height:26, border:`1px solid ${C.border}`, background:C.white, borderRadius:'6px', color:C.textMid, cursor:'pointer', fontWeight:700, fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>{lbl}</button>
                    ) : (
                      <React.Fragment key={lbl}>
                        <span style={{ width:28, textAlign:'center', fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'0.95rem', color:C.teal }}>{cardCopies}</span>
                        <button onClick={fn} style={{ width:26, height:26, border:`1px solid ${C.border}`, background:C.white, borderRadius:'6px', color:C.textMid, cursor:'pointer', fontWeight:700, fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>{lbl}</button>
                      </React.Fragment>
                    )
                  ))}
                </div>
                <button onClick={() => printPatientCard(opdRecord, token, deptName, selectedPatient, cardCopies)}
                  style={{ marginLeft:'6px', background:C.teal, color:C.white, border:'none', borderRadius:'8px', padding:'6px 14px', fontWeight:700, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Print Cards
                </button>
              </div>

              <PatientSummaryCard patient={selectedPatient} S={S} C={C} />
            </div>
          )}

          {/* ── STEP 1: Empty state + Waiting Room ── */}
          {step === 1 && (
            <>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:C.muted, textAlign:'center', padding:'2.5rem 3rem 1.5rem' }}>
                <div style={{ width:64, height:64, borderRadius:'16px', background:C.tealLight, border:`1px solid ${C.tealMid}`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'1rem' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'1.1rem', color:C.text }}>Search or register a patient</div>
                <div style={{ fontSize:'0.82rem', marginTop:'0.4rem', color:C.muted }}>Use the left panel to find an existing patient or register a new one</div>
              </div>

              {departments.length > 0 && (
                <WaitingRoomPanel departments={departments} avgConsultMins={10} />
              )}
            </>
          )}
        </main>
      </div>

      {/* Queue Detail Modal */}
      {queueModalDept && (
        <QueueDetailModal dept={queueModalDept} onClose={() => setQueueModalDept(null)} avgConsultMins={10} />
      )}

      {/* Scan Modal */}
      {showScanModal && (
        <PatientCardScanModal onClose={() => setShowScanModal(false)} />
      )}
    </div>
  );
};

/* ─── Sub-components ─── */
const InfoBlock = ({ label, value, C }) => (
  <div style={{ background:C.tealLight, border:`1px solid ${C.tealMid}`, borderRadius:'8px', padding:'0.6rem 0.8rem' }}>
    <div style={{ fontSize:'0.68rem', color:C.muted, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'2px' }}>{label}</div>
    <div style={{ fontSize:'0.88rem', fontWeight:600, color:C.text }}>{value || '—'}</div>
  </div>
);

/* InfoBlock variant for use on dark (teal gradient) token card */
const InfoBlockLight = ({ label, value }) => (
  <div style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:'8px', padding:'0.6rem 0.8rem', backdropFilter:'blur(4px)' }}>
    <div style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.65)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'2px' }}>{label}</div>
    <div style={{ fontSize:'0.88rem', fontWeight:600, color:'#ffffff' }}>{value || '—'}</div>
  </div>
);

const PatientSummaryCard = ({ patient, S, C }) => {
  if (!patient) return null;
  return (
    <div style={S.cardHighlight} className="opd-fade-in">
      <div style={S.sectionLabel}>Patient Details</div>
      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1rem' }}>
        <div style={{ ...S.patientAvatar, width:'46px', height:'46px', fontSize:'1.1rem' }}>
          {patient.first_name?.[0]}{patient.last_name?.[0]}
        </div>
        <div>
          <div style={{ fontWeight:700, fontSize:'1rem', color:C.text }}>{patient.first_name} {patient.last_name}</div>
          <div style={{ fontSize:'0.78rem', color:C.muted }}>{patient.patient_id}</div>
        </div>
        {patient.blood_group && (
          <span style={{ marginLeft:'auto', background:C.redLight, border:'1px solid #fecaca', borderRadius:'6px', padding:'4px 10px', color:C.red, fontSize:'0.8rem', fontWeight:700 }}>
            {patient.blood_group}
          </span>
        )}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.6rem' }}>
        {patient.phone         && <InfoBlock label="Phone"     value={patient.phone} C={C} />}
        {patient.gender        && <InfoBlock label="Gender"    value={patient.gender==='M'?'Male':patient.gender==='F'?'Female':'Other'} C={C} />}
        {patient.age           && <InfoBlock label="Age"       value={`${patient.age} yrs`} C={C} />}
        {patient.aadhar_number && <InfoBlock label="Aadhaar"   value={`XXXX-XXXX-${patient.aadhar_number.slice(-4)}`} C={C} />}
        {patient.abha_number   && <InfoBlock label="ABHA"      value={patient.abha_number} C={C} />}
        {patient.allergies     && <InfoBlock label="Allergies" value={patient.allergies} C={C} />}
      </div>
    </div>
  );
};

export default Recepsonist;